import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { COLORS } from '../theme';

const COL = '1.5fr 1.5fr 85px 1fr 90px 1.5fr 150px';
const HEADERS = ['Referred User', 'Ambassador', 'Signed Up', 'Milestones', 'AI Rec', 'AI Reason', 'Actions'];

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function Skeleton() {
  return (
    <div style={{ height: '13px', backgroundColor: COLORS.surfaceHover, borderRadius: '4px', width: '70%', animation: 'pulse 1.4s ease-in-out infinite' }} />
  );
}

function MilestoneCell({ r }) {
  const items = [
    { key: 'E', label: 'Email verified', active: r.emailVerified },
    { key: 'P', label: 'Phone verified', active: r.phoneVerified },
    { key: 'Pro', label: 'Profile complete', active: r.profileComplete },
    { key: 'Job', label: 'Job applied', active: !!r.firstJobAppliedAt },
  ];
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      {items.map(item => (
        <span
          key={item.key}
          title={item.label}
          style={{
            fontSize: '10px',
            fontWeight: '600',
            padding: '2px 5px',
            borderRadius: '3px',
            backgroundColor: item.active ? COLORS.success + '26' : COLORS.surfaceHover,
            color: item.active ? COLORS.success : COLORS.textMuted,
          }}
        >
          {item.key}
        </span>
      ))}
    </div>
  );
}

function AiRecCell({ r, expanded, onToggle }) {
  const colMap = { qualify: COLORS.success, deny: COLORS.danger, review: COLORS.warning };
  if (!r.aiRecommendation) return <span style={{ color: COLORS.textMuted, fontSize: '12px' }}>—</span>;
  return (
    <span
      onClick={e => { e.stopPropagation(); onToggle(); }}
      style={{ cursor: 'pointer', color: colMap[r.aiRecommendation] || COLORS.textSecondary, fontSize: '12px', fontWeight: '600', userSelect: 'none' }}
    >
      {r.aiRecommendation}
      {r.aiConfidence && <span style={{ color: COLORS.textMuted, fontWeight: '400', fontSize: '11px' }}> ({r.aiConfidence})</span>}
      <span style={{ marginLeft: '4px', fontSize: '10px', color: COLORS.textMuted }}>{expanded ? '▲' : '▼'}</span>
    </span>
  );
}

const INPUT_STYLE = {
  color: '#1F2937', backgroundColor: 'white', border: '1px solid #D1D5DB',
  padding: '8px 12px', borderRadius: '6px', fontSize: '14px',
  width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
};

export default function ReferralReviewsPage() {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [referredNames, setReferredNames] = useState({});
  const [ambassadorInfo, setAmbassadorInfo] = useState({});
  const fetchedReferredRef = useRef(new Set());
  const fetchedAmbassadorRef = useRef(new Set());

  const [expandedReasons, setExpandedReasons] = useState(new Set());

  // modal: { type: 'approve'|'deny', id }
  const [modal, setModal] = useState(null);
  const [modalInput, setModalInput] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  const [toast, setToast] = useState('');

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  }

  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'ambassador_referrals'), where('reviewStatus', '==', 'pending')),
      snap => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => (a.signedUpAt?.toMillis?.() || 0) - (b.signedUpAt?.toMillis?.() || 0));
        setReferrals(docs);
        setLoading(false);

        // Secondary reads — referred user display names (deduped)
        const newReferredUids = docs.map(d => d.referredUid).filter(id => id && !fetchedReferredRef.current.has(id));
        if (newReferredUids.length > 0) {
          newReferredUids.forEach(id => fetchedReferredRef.current.add(id));
          Promise.all(
            newReferredUids.map(id =>
              getDoc(doc(db, 'users', id)).then(s => {
                if (!s.exists()) return [id, id.slice(0, 8)];
                const data = s.data();
                return [id, data.displayName || [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email || id.slice(0, 8)];
              })
            )
          ).then(entries => setReferredNames(prev => ({ ...prev, ...Object.fromEntries(entries) })));
        }

        // Secondary reads — ambassador display name + ambassador ID (deduped)
        const newAmbUids = docs.map(d => d.ambassadorUid).filter(id => id && !fetchedAmbassadorRef.current.has(id));
        if (newAmbUids.length > 0) {
          newAmbUids.forEach(id => fetchedAmbassadorRef.current.add(id));
          Promise.all(
            newAmbUids.map(id =>
              getDoc(doc(db, 'users', id)).then(s => {
                if (!s.exists()) return [id, { name: id.slice(0, 8), ambassadorId: '—' }];
                const data = s.data();
                const name = data.displayName || [data.firstName, data.lastName].filter(Boolean).join(' ') || data.email || id.slice(0, 8);
                return [id, { name, ambassadorId: data.ambassador?.ambassadorId || '—' }];
              })
            )
          ).then(entries => setAmbassadorInfo(prev => ({ ...prev, ...Object.fromEntries(entries) })));
        }
      }
    );
    return () => unsub();
  }, []);

  function toggleReason(id) {
    setExpandedReasons(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function openModal(type, id) {
    setModal({ type, id });
    setModalInput('');
    setModalError('');
  }
  function closeModal() { setModal(null); setModalInput(''); setModalError(''); }

  async function handleApprove() {
    setModalLoading(true); setModalError('');
    try {
      await httpsCallable(functions, 'reviewReferral')({ referralId: modal.id, decision: 'approved' });
      showToast('Referral approved');
      closeModal();
    } catch (err) {
      setModalError(err.message || 'Action failed');
    } finally { setModalLoading(false); }
  }

  async function handleDeny() {
    if (!modalInput.trim()) { setModalError('Reason is required'); return; }
    setModalLoading(true); setModalError('');
    try {
      await httpsCallable(functions, 'reviewReferral')({ referralId: modal.id, decision: 'denied', reason: modalInput.trim() });
      showToast('Referral denied');
      closeModal();
    } catch (err) {
      setModalError(err.message || 'Action failed');
    } finally { setModalLoading(false); }
  }

  const headerCell = { fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 0', fontWeight: '600' };
  const cell = { padding: '11px 0', fontSize: '12px', color: COLORS.textPrimary };
  const actionBtn = color => ({ background: 'none', border: 'none', color, fontSize: '12px', cursor: 'pointer', padding: '0 8px 0 0', fontWeight: '500' });

  return (
    <div>
      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: COLORS.success, color: 'white',
          padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '500',
          zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {toast}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{ backgroundColor: COLORS.surface, borderRadius: '12px', padding: '28px', width: '420px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

            {modal.type === 'approve' && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '8px' }}>Approve Referral</div>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '20px' }}>
                  Mark this referral as qualified. This increments the ambassador's qualified count and may trigger a reward payout.
                </div>
              </>
            )}

            {modal.type === 'deny' && (
              <>
                <div style={{ fontSize: '16px', fontWeight: '700', color: COLORS.textPrimary, marginBottom: '8px' }}>Deny Referral</div>
                <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '16px' }}>Provide a reason for the denial.</div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '11px', color: COLORS.textSecondary, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>Reason *</label>
                  <textarea
                    value={modalInput}
                    onChange={e => setModalInput(e.target.value)}
                    placeholder="Explain why this referral is being denied…"
                    rows={3}
                    style={{ ...INPUT_STYLE, resize: 'vertical' }}
                    autoFocus
                  />
                </div>
              </>
            )}

            {modalError && <div style={{ fontSize: '12px', color: COLORS.danger, marginBottom: '12px' }}>{modalError}</div>}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={closeModal} disabled={modalLoading} style={{ padding: '8px 16px', fontSize: '13px', border: `1px solid ${COLORS.border}`, borderRadius: '6px', backgroundColor: 'transparent', color: COLORS.textSecondary, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={modal.type === 'approve' ? handleApprove : handleDeny}
                disabled={modalLoading}
                style={{
                  padding: '8px 16px', fontSize: '13px', fontWeight: '500', border: 'none', borderRadius: '6px', cursor: 'pointer',
                  backgroundColor: modal.type === 'deny' ? COLORS.danger : COLORS.success,
                  color: 'white', opacity: modalLoading ? 0.6 : 1,
                }}
              >
                {modalLoading ? '…' : modal.type === 'approve' ? 'Approve' : 'Deny'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header row */}
      <div style={{ marginBottom: '16px' }}>
        <span style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {loading ? '…' : referrals.length === 0
            ? 'No pending referrals to review'
            : `${referrals.length} referral${referrals.length !== 1 ? 's' : ''} pending review — oldest first`}
        </span>
      </div>

      {/* Table */}
      <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflowX: 'auto' }}>
        <div style={{ minWidth: '960px' }}>

          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '0 16px', borderBottom: `1px solid ${COLORS.border}` }}>
            {HEADERS.map(h => <div key={h} style={headerCell}>{h}</div>)}
          </div>

          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: COL, padding: '14px 16px', borderBottom: `1px solid ${COLORS.border}30` }}>
                {Array.from({ length: 7 }).map((__, j) => <div key={j}><Skeleton /></div>)}
              </div>
            ))
          ) : referrals.length === 0 ? (
            <div style={{ padding: '48px 16px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
              No pending referrals to review.
            </div>
          ) : (
            referrals.map((r, i) => (
              <div key={r.id}>
                {/* Row */}
                <div style={{
                  display: 'grid', gridTemplateColumns: COL, padding: '0 16px', alignItems: 'center',
                  borderBottom: expandedReasons.has(r.id) ? 'none' : (i < referrals.length - 1 ? `1px solid ${COLORS.border}30` : 'none'),
                }}>

                  {/* Referred user */}
                  <div style={{ ...cell, overflow: 'hidden' }}>
                    <div style={{ fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {referredNames[r.referredUid] || '…'}
                    </div>
                  </div>

                  {/* Ambassador */}
                  <div style={{ ...cell, overflow: 'hidden' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {ambassadorInfo[r.ambassadorUid]?.name || '…'}
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textMuted, fontFamily: 'monospace', marginTop: '2px' }}>
                      {ambassadorInfo[r.ambassadorUid]?.ambassadorId || ''}
                    </div>
                  </div>

                  {/* Signed up */}
                  <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '11px' }}>{fmtDate(r.signedUpAt)}</div>

                  {/* Milestones */}
                  <div style={cell}><MilestoneCell r={r} /></div>

                  {/* AI Rec */}
                  <div style={cell}><AiRecCell r={r} expanded={expandedReasons.has(r.id)} onToggle={() => toggleReason(r.id)} /></div>

                  {/* AI Reason (truncated, clickable) */}
                  <div
                    onClick={() => r.aiRecommendationReason && toggleReason(r.id)}
                    style={{
                      ...cell,
                      fontSize: '11px',
                      color: COLORS.textSecondary,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      paddingRight: '8px',
                      cursor: r.aiRecommendationReason ? 'pointer' : 'default',
                    }}
                  >
                    {r.aiRecommendationReason
                      ? r.aiRecommendationReason.slice(0, 60) + (r.aiRecommendationReason.length > 60 ? '…' : '')
                      : <span style={{ color: COLORS.textMuted }}>—</span>
                    }
                  </div>

                  {/* Actions */}
                  <div style={{ ...cell, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => openModal('approve', r.id)} style={actionBtn(COLORS.success)}>Approve</button>
                    <button onClick={() => openModal('deny', r.id)} style={actionBtn(COLORS.danger)}>Deny</button>
                    <button onClick={() => navigate(`/ambassadors/${r.ambassadorUid}`)} style={actionBtn(COLORS.accent)}>Detail</button>
                  </div>
                </div>

                {/* Expanded AI reason panel */}
                {expandedReasons.has(r.id) && r.aiRecommendationReason && (
                  <div style={{
                    padding: '6px 16px 14px',
                    borderBottom: i < referrals.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
                    backgroundColor: COLORS.bg + 'aa',
                  }}>
                    <div style={{ fontSize: '11px', color: COLORS.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '600', marginBottom: '4px' }}>AI Reasoning</div>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary, lineHeight: '1.5' }}>
                      {r.aiRecommendationReason}
                    </div>
                    {r.aiRecommendedAt && (
                      <div style={{ fontSize: '11px', color: COLORS.textMuted, marginTop: '4px' }}>Evaluated {fmtDate(r.aiRecommendedAt)}</div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
